/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Heading, Stack, Spinner, DisplayHtmlContent } from 'sarsaparilla';
import get from 'lodash/get';
import toString from 'lodash/toString';

import { LOWERDESCHUTES_PERMIT_ID } from 'ui-lowerdeschutes/src/constants';

import PermitMap from '../../src/shared/components/PermitMap';
import {
    BOUNDARY_WATERS_ID,
    GRAND_TETON_ID,
    IS_LAND_PERMIT_V2,
} from '../../src/constants';

const MAP_STYLE = {
    position: 'relative',
    backgroundColor: '#e3e3e3',
    backgroundRepeat: 'none',
    backgroundSize: 'cover',
    backgroundPosition: '50%',
    height: '35rem',
};

function DivisionDescription({ division }) {
    return <DisplayHtmlContent html={get(division, 'description', '')} />;
}

DivisionDescription.propTypes = {
    division: PropTypes.object,
};

function DivisionNeedToKnow({ division }) {
    return division?.description_map?.need_to_know ? (
        <Stack>
            <Heading headingLevel={2} appearance="h5">
                Need to know
            </Heading>
            <DisplayHtmlContent html={division?.description_map?.need_to_know} />
        </Stack>
    ) : null;
}

DivisionNeedToKnow.propTypes = {
    division: PropTypes.object,
};

const filterFeatures = (geoDataGeometry, divisionId) => {
    const features = geoDataGeometry?.features;
    if (!features?.length) {
        return undefined;
    }

    const divisionFeature = features.find((feature) => {
        const featureId = feature?.properties?.id
            ? toString(feature?.properties?.id)
            : null;
        return !!featureId && divisionId === featureId;
    });

    if (divisionFeature) {
        return {
            ...geoDataGeometry,
            features: [divisionFeature],
        };
    }
    return undefined;
};

function DivisionMap({ division, permitMapping }) {
    const [geoData, setGeoData] = useState({});
    const [points, setPoints] = useState();
    const [lines, setLines] = useState();
    const [polygons, setPolygons] = useState();

    const divisionId = division?.id ? toString(division?.id) : null;
    const permitId = division.permit_id;

    const fetchMapPointers = async () => {
        try {
            let url = `/shared/permit/data/${permitId}/features.json`;
            if (IS_LAND_PERMIT_V2(permitId, permitMapping)) {
                url = `/shared/land-permit/data/${permitId}/features.json`;
            }
            if (permitId === LOWERDESCHUTES_PERMIT_ID) {
                url = `/shared/lowerdeschutes/data/${permitId}/features.json`;
            }
            if (permitId === BOUNDARY_WATERS_ID) {
                url = `/shared/boundarywaters/data/${permitId}/features.json`;
            }
            if (permitId === GRAND_TETON_ID) {
                url = `/shared/grandteton/data/${permitId}/features.json`;
            }

            const response = await axios.get(url);
            setGeoData(response.data);
        } catch {
            // dont render map
        }
    };

    useEffect(() => {
        if (divisionId) {
            fetchMapPointers();
        }
    }, [divisionId]);

    useEffect(() => {
        setPoints(filterFeatures(geoData?.points, divisionId));
        setLines(filterFeatures(geoData?.lines, divisionId));
        setPolygons(filterFeatures(geoData?.polygons, divisionId));
    }, [geoData, divisionId]);

    if (!divisionId || (!points && !lines && !polygons)) {
        return null;
    }

    return (
        <div className="permit-division-map" id="division-map" style={MAP_STYLE}>
            <PermitMap
                fullscreenControl={false}
                geoJsonPoints={points}
                geoJsonLines={lines}
                geoJsonPolygons={polygons}
                mapContainer="division-map"
                zoom={17}
            />
        </div>
    );
}

DivisionMap.propTypes = {
    division: PropTypes.object,
    permitMapping: PropTypes.object,
};

export default function DivisionDetail({ permit, divisionId, hasMap, permitMapping }) {
    const division = permit?.divisions?.[divisionId];

    if (!division) {
        return <Spinner isCentered size={'xl'} />;
    }

    return (
        <Stack>
            <DivisionDescription division={division} />
            {hasMap && <DivisionMap division={division} permitMapping={permitMapping} />}
            <DivisionNeedToKnow division={division} />
        </Stack>
    );
}

DivisionDetail.propTypes = {
    permit: PropTypes.object,
    divisionId: PropTypes.string,
    hasMap: PropTypes.bool,
    permitMapping: PropTypes.object,
};
